import { AsyncPipe, I18nPluralPipe, NgClass } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import moment, { Moment } from "moment";
import {
  createResolveBundle,
  ResolveBundle,
  ResolveModule,
} from "resolve-module";
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  race,
  Subject,
  take,
  takeUntil,
  tap,
} from "rxjs";
import {
  RequestedCategory,
  RecurringActionType,
  TimeBlocker,
  TimeFormat,
} from "types";

import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@db-ui/button";
import { ButtonIconComponent } from "@db-ui/button-icon";
import { HtmlContainerComponent } from "@db-ui/html-container";
import { TextInputComponent } from "@db-ui/text-input";
import { UserInfoComponent } from "@db-ui/user-info";
import { HUMAN_DATE_FORMAT } from "shared-constants";
import { ExecPipe } from "shared-pipes";
import { MeetingRoomsKioskModel } from "../../+store/model";
import { MeetingRoomKioskBookingFormComponent } from "../meeting-room-kiosk-booking-form/meeting-room-kiosk-booking-form.component";

import { distinctUntilChangedDiff } from "projects/common-module/src/lib/rxjs-operators/distinct-until-changed-diff";
import {
  IKioskScheduleEntry,
  IMeetingRoomKioskBookingCreationData,
  IMeetingRoomKioskEventData,
  KioskScheduleEntrySize,
  MeetingRoomKioskStatus,
  PlatformEnum,
} from "../../models/kiosk.model";
import { FullscreenService } from "../../services/fullscreen.service";
import { LedActivationService } from "../../services/led-activation.service";
import { WakeLockService } from "../../services/wake-lock.service";
import { generateKioskTimeBlockersForDay } from "../../utils/kiosk.utils";
import { MeetingRoomKioskBookingForm } from "../meeting-room-kiosk-booking-form/meeting-room-kiosk-booking-form";
import {
  KioskLocalStorageKeysEnum,
  LocalStorageService,
} from "../../services/local-storage.service";
import { formatHourAndMinutes } from "projects/shared/src/lib/utils/time-formatter";
const AUTO_REFRESH_INTERVAL = 30_000;

@Component({
  selector: "db-meeting-room-kiosk",
  templateUrl: "./meeting-room-kiosk.component.html",
  styleUrls: ["./meeting-room-kiosk.component.scss"],
  standalone: true,
  imports: [
    HtmlContainerComponent,
    ButtonIconComponent,
    ButtonComponent,
    ResolveModule,
    AsyncPipe,
    ExecPipe,
    UserInfoComponent,
    I18nPluralPipe,
    MeetingRoomKioskBookingFormComponent,
    FormsModule,
    NgClass,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingRoomKioskComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly kioskModel = inject(MeetingRoomsKioskModel);
  private readonly fullscreenService = inject(FullscreenService);
  private readonly wakeLockService = inject(WakeLockService);
  private readonly ledActivationService = inject(LedActivationService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly MeetingRoomKioskStatus = MeetingRoomKioskStatus;
  readonly KioskScheduleEntrySize = KioskScheduleEntrySize;
  readonly RecurringActionType = RecurringActionType;
  readonly PlatformEnum = PlatformEnum;

  @ViewChild(MeetingRoomKioskBookingFormComponent)
  kioskForm!: MeetingRoomKioskBookingFormComponent;
  @ViewChild("codeInput") codeInput!: TextInputComponent;

  readonly defaultTitleLabel = $localize`:@@meeting-room-booking-details-form|default-meeting-title:Meeting`;
  readonly privateTitleLabel = $localize`:@@meeting-room-booking-details-form|default-private-meeting-title:Occupied`;
  readonly slotFreeNowLabel = $localize`:@@meeting-rooms-kiosk|slot-free-now-label:Free now`;
  readonly slotFreeLabel = $localize`:@@meeting-rooms-kiosk|slot-free-label:Free`;
  readonly slotOccupiedLabel = $localize`:@@meeting-rooms-kiosk|slot-occupied-label:Occupied`;
  readonly slotAwaitingCheckInLabel = $localize`:@@meeting-rooms-kiosk|slot-awaiting-check-in-label:Awaiting Check-In`;

  readonly requestedServiceCategoriesPluralMap: { [k: string]: string } = {
    "=0": $localize`:@@meeting-rooms-kiosk|request-service-count.zero:0 services requested`,
    "=1": $localize`:@@meeting-rooms-kiosk|request-service-count.one:1 service requested`,
    few: $localize`:@@meeting-rooms-kiosk|request-service-count.few:# services requested`,
    many: $localize`:@@meeting-rooms-kiosk|request-service-count.many:# services requested`,
    other: $localize`:@@meeting-rooms-kiosk|request-service-count.other:# services requested`,
  };

  readonly statusToLabelMap: { [key in MeetingRoomKioskStatus]: string } = {
    [MeetingRoomKioskStatus.FREE]: this.slotFreeLabel,
    [MeetingRoomKioskStatus.OCCUPIED]: this.slotOccupiedLabel,
    [MeetingRoomKioskStatus.CHECK_IN]: this.slotAwaitingCheckInLabel,
  };

  @HostListener("window:resize", ["$event.target.innerWidth"])
  onResize(width: number) {
    this.smallScreenCheck(width);
  }

  private killSubscriptions$$: Subject<void> = new Subject<void>();
  private dataLoad$$ = new BehaviorSubject<boolean>(false);
  private eventDetailsDataLoad$$ = new BehaviorSubject<string | null>(null);

  officeId: string = "";
  meetingRoomId: string = "";

  today = moment();
  currentTimeIndicatorString!: string;
  currentClockString!: string;
  currentDateString!: string;
  timezone!: string;
  is12HourFormat: boolean = false;

  timeInterval!: number;
  dataRefreshInterval!: number;
  hours!: number[];

  isOnSmallScreen: boolean = false;
  roomInformationVisible = false;
  newBookingSlot: IKioskScheduleEntry | null = null;
  initialMeetingRoomBookingDetailsValue: MeetingRoomKioskBookingForm["value"] =
    {};
  meetingRoomKioskBookingValue: MeetingRoomKioskBookingForm["value"] = {};
  selectedScheduleEntry: IKioskScheduleEntry | null = null;
  isCheckingBookingIn = false;
  isEndingBookingEarly = false;
  platform: PlatformEnum = PlatformEnum.WEB;
  isFullScreen = false;
  eventsCount = 0;

  private readonly HOUR_HEIGHT = 180; // Each hour block set height

  meetingRoomId$ = this.route.params.pipe(
    map((params) => params["meetingRoomId"] as string),
    filter(Boolean),
  );
  hourFormat$ = this.route.queryParams.pipe(
    map((params) => params["hourFormat"] as TimeFormat),
    filter(Boolean),
  );

  kioskData$ = this.kioskModel.selectors.meetingRoomKioskData$;
  eventDetails$ = this.kioskModel.selectors.eventDetails$;

  meetingRoomData$ = this.kioskData$.pipe(
    map((meetingRoomsKioskData) => meetingRoomsKioskData?.meetingRoom),
  );
  timezone$ = this.meetingRoomData$.pipe(
    filter(Boolean),
    map((mr) => mr.timezone),
  );
  meetingRoomEvents$ = this.kioskData$.pipe(
    filter(Boolean),
    map((meetingRoomsKioskData) => meetingRoomsKioskData.events || []),
  );

  // Create a helper method
  private getTimeInRoomTimezone(): moment.Moment {
    return moment().tz(this.timezone);
  }

  meetingRoomScheduleData$ = combineLatest([
    this.meetingRoomEvents$.pipe(filter(Boolean)),
    this.meetingRoomData$.pipe(filter(Boolean)),
  ]).pipe(
    map(([events, meetingRoomData]) => {
      this.eventsCount = events.length;
      const now = this.getTimeInRoomTimezone();
      const isCheckInEnabled = meetingRoomData.checkIn.enabled;

      // Map existing events to schedule entries
      const eventEntries: IKioskScheduleEntry[] = events.map((event) => {
        const startMoment = moment(event.startTime);
        const endMoment = moment(event.endTime);
        const size = this.getScheduleEntrySize(startMoment, endMoment);
        const duration = endMoment.diff(startMoment, "minutes");
        const isPastEvent = endMoment.isBefore(now);

        return {
          id: event.id,
          status: isPastEvent
            ? MeetingRoomKioskStatus.OCCUPIED
            : event.checkIn?.checkedIn === true || !isCheckInEnabled
              ? MeetingRoomKioskStatus.OCCUPIED
              : MeetingRoomKioskStatus.CHECK_IN,
          startMoment: startMoment,
          startTime: event.startTime,
          endTime: event.endTime,
          title: event.subject || this.privateTitleLabel,
          event,
          organiser: event.owner,
          hasCatering: event.hasServiceRequest,
          size,
          isPast: isPastEvent,
          duration,
        };
      });

      // Create free time slots
      const freeSlots: IKioskScheduleEntry[] = [];
      let freeEventCount = 0;
      const dayStart = now.clone().startOf("day");
      const dayEnd = now.clone().endOf("day");

      // Sort events chronologically for easier processing
      const sortedEvents = [...events].sort((a, b) =>
        moment(a.startTime).diff(moment(b.startTime)),
      );

      // Handle empty day case
      if (sortedEvents.length === 0) {
        freeSlots.push({
          id: `free_${freeEventCount++}`,
          status: MeetingRoomKioskStatus.FREE,
          startMoment: dayStart,
          startTime: dayStart.toISOString(),
          endTime: dayEnd.toISOString(),
          title: this.slotFreeLabel,
          size: this.getScheduleEntrySize(dayStart, dayEnd),
          duration: dayEnd.diff(dayStart, "minutes"),
        });
      } else {
        // Add free slot at start of day if needed
        const firstEvent = sortedEvents[0];
        const firstEventStart = moment(firstEvent.startTime);

        if (firstEventStart.isAfter(dayStart)) {
          freeSlots.push({
            id: `free_${freeEventCount++}`,
            status: MeetingRoomKioskStatus.FREE,
            startMoment: dayStart,
            startTime: dayStart.toISOString(),
            endTime: firstEvent.startTime,
            title: this.slotFreeLabel,
            size: this.getScheduleEntrySize(dayStart, firstEventStart),
            duration: firstEventStart.diff(dayStart, "minutes"),
          });
        }

        // Add free slots between events
        for (let i = 0; i < sortedEvents.length - 1; i++) {
          const currentEvent = sortedEvents[i];
          const nextEvent = sortedEvents[i + 1];
          const currentEnd = moment(currentEvent.endTime);
          const nextStart = moment(nextEvent.startTime);

          if (currentEnd.isBefore(nextStart)) {
            freeSlots.push({
              id: `free_${freeEventCount++}`,
              status: MeetingRoomKioskStatus.FREE,
              startMoment: currentEnd,
              startTime: currentEvent.endTime,
              endTime: nextEvent.startTime,
              title: this.slotFreeLabel,
              size: this.getScheduleEntrySize(currentEnd, nextStart),
              duration: nextStart.diff(currentEnd, "minutes"),
            });
          }
        }

        // Add free slot at end of day if needed
        const lastEvent = sortedEvents[sortedEvents.length - 1];
        const lastEventEnd = moment(lastEvent.endTime);

        if (lastEventEnd.isBefore(dayEnd)) {
          freeSlots.push({
            id: `free_${freeEventCount++}`,
            status: MeetingRoomKioskStatus.FREE,
            startMoment: lastEventEnd,
            startTime: lastEvent.endTime,
            endTime: dayEnd.toISOString(),
            title: this.slotFreeLabel,
            size: this.getScheduleEntrySize(lastEventEnd, dayEnd),
            duration: dayEnd.diff(lastEventEnd, "minutes"),
          });
        }
      }

      // Combine and sort all entries
      const allEntries = [...eventEntries, ...freeSlots].sort(
        (a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf(),
      );

      return allEntries;
    }),
    distinctUntilChangedDiff(),
    tap(() => {
      this.isCreatingBooking = false;
      setTimeout(() => this.scrollToCurrentEvent(), 100);
    }),
  );

  private statusRefreshTrigger$ = new BehaviorSubject<void>(undefined);

  firstPairOfMeetingRoomScheduleEntries$ = combineLatest([
    this.meetingRoomScheduleData$.pipe(filter(Boolean)),
    this.statusRefreshTrigger$.pipe(),
  ]).pipe(
    map(([data, _]) => {
      const now = this.getTimeInRoomTimezone();
      const currentEntry = data.find((entry) => {
        const startTime =
          entry.startMoment ?? this.parseTimeWithRoomTimezone(entry.startTime);
        const endTime = this.parseTimeWithRoomTimezone(entry.endTime);
        return now.isSameOrAfter(startTime) && now.isBefore(endTime);
      });

      const activeEntry = currentEntry || data[0];
      const nextEntryIndex = currentEntry ? data.indexOf(currentEntry) + 1 : 1;
      const nextEntry =
        nextEntryIndex < data.length ? data[nextEntryIndex] : null;

      return [activeEntry, nextEntry].filter(Boolean) as [
        IKioskScheduleEntry,
        IKioskScheduleEntry?,
      ];
    }),
    filter((entries) => !!entries.length),
    tap((entries) => {
      this.ledActivationService.setRoomLedStatus(
        entries[0]?.status || MeetingRoomKioskStatus.FREE,
      );
    }),
  );

  isAvailableUntilEOD$ = this.meetingRoomScheduleData$.pipe(
    filter(Boolean),
    map(
      (data) =>
        data.length === 1 && data[0].status === MeetingRoomKioskStatus.FREE,
    ),
  );

  readonly kioskResolveBundle: ResolveBundle[] = [
    createResolveBundle(this.dataLoad$$.pipe(filter(Boolean)))({
      dispatchRequest: () => {
        this.kioskModel.actions.dispatch.loadKioskMode({
          roomId: this.meetingRoomId,
        });
      },
      dispatchRequestCancel: () => {
        this.kioskModel.actions.dispatch.loadKioskModeCancel();
      },
      requestSuccess$:
        this.kioskModel.actions.listen.loadKioskModeSuccess$.pipe(
          tap(() => {
            // Scroll to current event after data refresh
            setTimeout(() => this.scrollToCurrentEvent(), 300);
          }),
        ),
      requestFailure$: this.kioskModel.actions.listen.loadKioskModeCancel$,
    }),
  ];

  readonly eventDetailsResolveBundle: ResolveBundle[] = [
    createResolveBundle(this.eventDetailsDataLoad$$.pipe(filter(Boolean)))({
      dispatchRequest: (eventId) => {
        this.kioskModel.actions.dispatch.loadEventDetails({
          roomId: this.meetingRoomId,
          eventId,
        });
      },
      dispatchRequestCancel: () => {
        this.kioskModel.actions.dispatch.loadEventDetailsCancel();
      },
      requestSuccess$: this.kioskModel.actions.listen.loadEventDetailsSuccess$,
      requestFailure$: this.kioskModel.actions.listen.loadEventDetailsCancel$,
    }),
  ];

  formatHourString = (hour: number, minutes = 0): string => {
    return formatHourAndMinutes(this.is12HourFormat, hour, minutes);
  };

  formatTimeString = (time: string) => {
    const timeMoment = moment(time);
    return formatHourAndMinutes(
      this.is12HourFormat,
      timeMoment.get("hours"),
      timeMoment.get("minute"),
    );
  };

  // Current time indicator position in pixels
  currentTimeIndicatorPosition: number = 0;

  // Store the last known schedule data as a class property
  private lastKnownScheduleData: IKioskScheduleEntry[] = [];

  ngOnInit(): void {
    this.setupTimezone();
    this.getCurrentPlatform();
    this.smallScreenCheck(window.innerWidth);
    this.setupInitialData();
    this.setupScheduleData();
    this.setupFullscreenListener();
  }

  private fullscreenChangeHandler = () => {
    this.isFullScreen = !!document.fullscreenElement;
    this.cdr.detectChanges();
  };

  private setupFullscreenListener(): void {
    document.addEventListener("fullscreenchange", this.fullscreenChangeHandler);
  }

  getCurrentPlatform(): void {
    this.platform =
      (this.localStorageService.getItem(
        KioskLocalStorageKeysEnum.PLATFORM,
      ) as PlatformEnum) || PlatformEnum.WEB;
  }

  enterFullScreen(): void {
    this.fullscreenService.requestFullscreen();
    this.isFullScreen = true;
  }

  startTimeUpdateInterval() {
    this.timeInterval = setInterval(() => {
      this.updateTimeAndDate();
    }, 60_000) as unknown as number;
  }

  // Update this method to track the schedule data
  setupScheduleData(): void {
    this.meetingRoomScheduleData$
      .pipe(filter(Boolean), takeUntil(this.killSubscriptions$$))
      .subscribe((scheduleData) => {
        // Store the schedule data for use in scrolling
        this.lastKnownScheduleData = scheduleData;

        // Create array of hours for full day (0-23)
        this.hours = new Array(24).fill(null).map((_, idx) => idx);
        this.updateTimeAndDate();

        // Add a delayed scroll to make sure the UI is updated
        setTimeout(() => this.scrollToCurrentEvent(), 300);
      });
  }

  setupTimezone(): void {
    this.timezone$
      .pipe(distinctUntilChanged(), takeUntil(this.killSubscriptions$$))
      .subscribe((timezone) => {
        this.timezone = timezone;
        this.startTimeUpdateInterval();
      });
  }

  private updateTimeAndDate(): void {
    const now = moment().tz(this.timezone);
    const minutes = now.minutes();
    this.currentClockString = now.format(
      this.is12HourFormat ? "h:mm A" : "HH:mm",
    );
    // Only show time if not within 8 minutes of a round hour (before or after)
    if (minutes >= 8 && minutes <= 52) {
      this.currentTimeIndicatorString = now.format(
        this.is12HourFormat ? "h:mm A" : "HH:mm",
      );
    } else {
      // Hide time string when too close to hour markers
      this.currentTimeIndicatorString = "";
    }
    this.currentDateString = now.format("dddd, DD MMMM YYYY");

    // Update the current time indicator position
    this.updateCurrentTimeIndicatorPosition(now);

    // Trigger status refresh with each time update
    this.statusRefreshTrigger$.next();

    // Scroll to current time indicator after updating position
    // Add a small delay to ensure UI updates first
    setTimeout(() => this.scrollToCurrentEvent(), 100);

    this.cdr.detectChanges();
  }

  /**
   * Calculate the position of the current time indicator based on the current time
   * relative to the displayed schedule hours
   */
  private updateCurrentTimeIndicatorPosition(now: moment.Moment): void {
    // Get current hour and minutes
    const currentHour = now.hour();
    const currentMinute = now.minute();

    // Calculate position: (hours difference + minutes as fraction of hour) * hourHeight
    // Assuming schedule starts visually at hour 0
    this.currentTimeIndicatorPosition =
      (currentHour + currentMinute / 60) * this.HOUR_HEIGHT;
  }

  setupInitialData(): void {
    this.meetingRoomId$
      .pipe(
        takeUntil(this.killSubscriptions$$),
        tap((roomId) => {
          this.meetingRoomId = roomId;
        }),
      )
      .subscribe(() => {
        this.dataLoad$$.next(true);
        this.startDataRefresh();
      });
  }

  startDataRefresh() {
    if (this.dataRefreshInterval) {
      clearInterval(this.dataRefreshInterval);
    }

    this.dataRefreshInterval = setInterval(() => {
      this.dataLoad$$.next(true);
    }, AUTO_REFRESH_INTERVAL) as unknown as number;
  }

  ngOnDestroy() {
    clearInterval(this.timeInterval);
    clearInterval(this.dataRefreshInterval);

    this.killSubscriptions$$.next();
    this.killSubscriptions$$.complete();
    this.fullscreenService.exitFullscreen();
    this.isFullScreen = false;
    document.removeEventListener(
      "fullscreenchange",
      this.fullscreenChangeHandler,
    );
  }

  smallScreenCheck = (width: number) => {
    this.isOnSmallScreen = width <= 1000;
  };

  calculateInnerEventHeight = (event: IKioskScheduleEntry): number => {
    const start = moment(event.startTime);
    const end = moment(event.endTime);
    // Ensure at least 1 minute diff for calculation basis
    const diff = Math.max(1, end.diff(start, "minutes"));

    const durationHeight = (diff / 60) * this.HOUR_HEIGHT;

    // Subtract the 4px gap
    const heightMinusGap = durationHeight - 4;

    // Ensure a minimum visual height of 1px after subtracting gap
    return Math.max(1, heightMinusGap);
  };

  // New: Calculates height for the outer wrapper (pure duration)
  calculateWrapperHeight = (event: IKioskScheduleEntry): number => {
    const start = moment(event.startTime);
    const end = moment(event.endTime);
    const diff = Math.max(1, end.diff(start, "minutes"));
    const durationHeight = (diff / 60) * this.HOUR_HEIGHT;
    return Math.max(1, durationHeight); // Minimum 1px wrapper height
  };

  /**
   * Calculate the absolute top position for an event based on its start time.
   */
  calculateEventTopPosition = (event: IKioskScheduleEntry): number => {
    const startMoment = this.parseTimeWithRoomTimezone(event.startTime);
    const startHour = startMoment.hour();
    const startMinute = startMoment.minute();

    // Calculate position: (start hour + minutes as fraction of hour) * hourHeight
    const position = (startHour + startMinute / 60) * this.HOUR_HEIGHT;

    return position;
  };

  handleEntryClick = (entry: IKioskScheduleEntry) => {
    // Don't allow interaction with past events except viewing details
    if (!entry.event && entry.status !== MeetingRoomKioskStatus.FREE) {
      return;
    }
    this.selectedScheduleEntry = entry;
    this.eventDetailsDataLoad$$.next(entry.id);
  };

  deselectEntry = () => {
    this.selectedScheduleEntry = null;
    this.eventDetailsDataLoad$$.next(null);
    this.kioskModel.actions.dispatch.loadEventDetailsCleanup();
  };

  private getScheduleEntrySize = (
    start: Moment,
    end: Moment,
  ): KioskScheduleEntrySize => {
    const diffInMinutes = end.diff(start, "minutes");
    if (diffInMinutes < 5) {
      return KioskScheduleEntrySize.NONE;
    }
    if (diffInMinutes <= 10) {
      return KioskScheduleEntrySize.MINI;
    }
    if (diffInMinutes <= 15) {
      return KioskScheduleEntrySize.X_SMALL;
    }
    if (diffInMinutes > 15 && diffInMinutes <= 30) {
      return KioskScheduleEntrySize.SMALL;
    }
    if (diffInMinutes <= 45) {
      return KioskScheduleEntrySize.MEDIUM;
    }
    return KioskScheduleEntrySize.LARGE;
  };

  getRequestedServiceCountFromCategories = (
    categories: RequestedCategory[],
  ): number =>
    categories.reduce((acc, curr) => acc + curr.requestedItems.length, 0);

  roundUpToNextMinutePeriod(momentObj: Moment, minutes: 15 | 30) {
    const remainder = minutes - (momentObj.minute() % minutes);
    return momentObj.clone().add(remainder, "minutes").startOf("minute");
  }

  onInitBook(slotIdx: number): void {
    combineLatest([
      this.meetingRoomScheduleData$,
      this.meetingRoomData$.pipe(filter(Boolean)),
    ])
      .pipe(take(1))
      .subscribe(([data, { timezone }]) => {
        const slot = data[slotIdx];
        if (slot.status !== MeetingRoomKioskStatus.FREE) {
          return;
        }
        this.newBookingSlot = slot;

        const now = moment();
        const nextAvailableStartTime = now.isAfter(slot.startMoment)
          ? this.roundUpToNextMinutePeriod(now, 15)
          : slot.startMoment;

        let startTime = nextAvailableStartTime.tz(timezone).format("HH:mm");
        const endTime = moment(slot.endTime).tz(timezone).format("HH:mm");

        if (startTime === endTime) {
          const newStartMoment = this.roundUpToNextMinutePeriod(
            now.subtract(15, "minutes"),
            15,
          );
          startTime = newStartMoment.format("HH:mm");
        }

        this.meetingRoomKioskBookingValue.startTime = startTime;
        this.meetingRoomKioskBookingValue.endTime = endTime;
      });
  }
  onCancelBook(): void {
    this.newBookingSlot = null;
    this.meetingRoomKioskBookingValue = {};
  }

  isCreatingBooking = false;
  onConfirmCreateBooking(): void {
    if (!this.kioskForm.form.value || this.isCreatingBooking) {
      return;
    }

    this.isCreatingBooking = true;

    const { title } = this.kioskForm.form.value;
    const data: IMeetingRoomKioskBookingCreationData = {
      subject: title || this.defaultTitleLabel,
      startTime: moment(this.meetingStartTime).format(HUMAN_DATE_FORMAT),
      endTime: moment(this.meetingEndTime).format(HUMAN_DATE_FORMAT),
    };

    this.kioskModel.actions.dispatch.createBooking({
      roomId: this.meetingRoomId,
      data,
    });

    race(
      this.kioskModel.actions.listen.createBookingSuccess$,
      this.kioskModel.actions.listen.createBookingFailure$.pipe(
        map(() => false),
      ),
    )
      .pipe(take(1))
      .subscribe((response) => {
        if (!response) {
          this.isCreatingBooking = false;
          return;
        }
        this.onCancelBook();
        this.dataLoad$$.next(true);
      });
  }

  onEndMeeting(slotIdx: number): void {
    this.meetingRoomScheduleData$.pipe(take(1)).subscribe((data) => {
      const slot = data[slotIdx];
      if (slot.status !== MeetingRoomKioskStatus.OCCUPIED || !slot.event?.id) {
        return;
      }

      this.isEndingBookingEarly = true;

      const endTime = moment().format(HUMAN_DATE_FORMAT);
      this.kioskModel.actions.dispatch.finishBooking({
        roomId: this.meetingRoomId,
        eventId: slot.event.id,
        endTime,
      });

      race(
        this.kioskModel.actions.listen.finishBookingSuccess$,
        this.kioskModel.actions.listen.finishBookingFailure$.pipe(
          map((error) => {
            console.error("End meeting failed:", error);
            return false;
          }),
        ),
      )
        .pipe(take(1))
        .subscribe((response) => {
          this.isEndingBookingEarly = false;
          if (!response) return;

          // Update the LED status
          this.ledActivationService.setRoomLedStatus(
            MeetingRoomKioskStatus.FREE,
          );

          // Force a reload of the data to get the updated status
          this.dataLoad$$.next(true);

          // Force change detection
          this.cdr.detectChanges();
        });
    });
  }

  onCheckInMeeting(slotIdx: number): void {
    this.meetingRoomScheduleData$.pipe(take(1)).subscribe((data) => {
      // Make sure we're using the correct slot based on the index
      const slot = data[slotIdx];

      // Verify this slot really needs check-in
      if (slot.status !== MeetingRoomKioskStatus.CHECK_IN || !slot.event?.id) {
        console.error(
          "Cannot check in: slot is not in CHECK_IN status or has no event ID",
        );
        return;
      }

      this.isCheckingBookingIn = true;

      this.kioskModel.actions.dispatch.checkInBooking({
        roomId: this.meetingRoomId,
        eventId: slot.event.id, // Make sure we use the actual event ID
      });

      race(
        this.kioskModel.actions.listen.checkInBookingSuccess$,
        this.kioskModel.actions.listen.checkInBookingFailure$.pipe(
          map((error) => {
            console.error("Check-in failed:", error);
            return false;
          }),
        ),
      )
        .pipe(take(1))
        .subscribe((response) => {
          this.isCheckingBookingIn = false;
          if (!response) return;

          // Update the LED status
          this.ledActivationService.setRoomLedStatus(
            MeetingRoomKioskStatus.OCCUPIED,
          );
          // reset the checking in event index (needed for loading state)
          this.checkingInEventIdx = null;
          // Force a reload of the data to get the updated status
          this.dataLoad$$.next(true);

          // In case the server update is slow, we can also update the local state
          // directly to provide immediate feedback to the user
          const updatedData = [...data];
          updatedData[slotIdx] = {
            ...updatedData[slotIdx],
            status: MeetingRoomKioskStatus.OCCUPIED,
          };

          // Force change detection
          this.cdr.detectChanges();
        });
    });
  }

  meetingStartTime!: number;
  meetingEndTime!: number;
  meetingTimeRangeInputChangeHandler({
    startTime,
    endTime,
    dateTimestamp,
  }: Pick<
    MeetingRoomKioskBookingForm["value"],
    "startTime" | "endTime" | "dateTimestamp"
  >): void {
    // if (!startTime || !endTime || !dateTimestamp || !this.meetingStartTime || !this.meetingEndTime) {
    if (!startTime || !endTime || !dateTimestamp) {
      return;
    }
    const [startHour, startMinute] = startTime.split(":");
    const [endHour, endMinute] = endTime.split(":");
    if (endTime === "00:15") {
      this.meetingStartTime = this.setSelectedTimeFromInputs(
        dateTimestamp,
        0,
        0,
      );
    } else {
      this.meetingStartTime = this.setSelectedTimeFromInputs(
        dateTimestamp,
        +startHour,
        +startMinute,
      );
    }
    if (startTime === "23:45") {
      this.meetingEndTime = this.setSelectedTimeFromInputs(
        dateTimestamp,
        23,
        59,
      );
    } else {
      this.meetingEndTime = this.setSelectedTimeFromInputs(
        dateTimestamp,
        +endHour,
        +endMinute,
      );
    }
    if (endTime === "00:00") {
      this.meetingEndTime = this.setSelectedTimeFromInputs(
        dateTimestamp,
        23,
        59,
      );
    }
  }
  private setSelectedTimeFromInputs(
    currentTime: number,
    hours: number,
    minutes: number,
  ) {
    return moment(currentTime)
      .clone()
      .tz(this.timezone)
      .set("hours", hours)
      .set("minutes", minutes)
      .toDate()
      .getTime();
  }

  generateTimeBlockersForDay = (
    dayMoment: moment.Moment | null,
  ): Observable<TimeBlocker[]> => {
    if (!dayMoment) {
      // TODO get rid of dayMoment
      return of([]);
    }

    return this.kioskData$.pipe(
      filter(Boolean),
      take(1),
      map((meetingRoomKioskData) =>
        generateKioskTimeBlockersForDay(meetingRoomKioskData),
      ),
    );
  };

  userNameIsTooLong(user?: IMeetingRoomKioskEventData["owner"]): boolean {
    if (!user) return false;

    const name = `${user.firstName} ${user.lastName}`;
    if (name.length > 40) return true;

    return false;
  }

  titleIsTooLong(subject?: IMeetingRoomKioskEventData["subject"]): boolean {
    if (!subject) return false;

    if (subject.length > 30) return true;

    return false;
  }

  exitKiosk(): void {
    this.kioskModel.actions.dispatch.exitKioskMode({
      roomId: this.meetingRoomId,
    });
    this.fullscreenService.exitFullscreen();
  }

  private scrollToCurrentEvent(): void {
    // Don't attempt to scroll if no schedule container exists
    const scheduleContainer = document.querySelector(".schedule");
    if (!scheduleContainer) {
      return;
    }

    const container = scheduleContainer as HTMLElement;
    const now = this.getTimeInRoomTimezone();

    // Find current entry from cached schedule data
    const currentEntry = this.findCurrentScheduleEntry(now);

    let targetPosition: number;

    if (!currentEntry) {
      // If no current entry found, just scroll to the current time indicator with offset
      targetPosition = Math.max(0, this.currentTimeIndicatorPosition - 100);
    } else {
      // Calculate meeting duration in hours
      const startTime = this.parseTimeWithRoomTimezone(currentEntry.startTime);
      const endTime = this.parseTimeWithRoomTimezone(currentEntry.endTime);
      const durationInHours = endTime.diff(startTime, "hours", true);

      // Calculate hours since meeting started
      const hoursSinceMeetingStart = now.diff(startTime, "hours", true);

      if (durationInHours <= 3) {
        // For shorter meetings (≤3h), scroll to the top of the meeting
        targetPosition = this.calculateEventTopPosition(currentEntry);
      } else {
        // For longer meetings (>3h)
        if (hoursSinceMeetingStart > 3) {
          // If we're 3+ hours into the meeting, use time indicator - 135px top offset
          targetPosition = this.currentTimeIndicatorPosition - 135;
        } else {
          // Otherwise, scroll to the top of the meeting
          targetPosition = this.calculateEventTopPosition(currentEntry);
        }
      }
    }

    // Ensure target position is valid
    targetPosition = Math.max(0, targetPosition);

    // Perform scroll
    this.animateSmoothScroll(container, targetPosition);
  }

  /**
   * Find the current schedule entry containing the given timestamp
   */
  private findCurrentScheduleEntry(
    now: moment.Moment,
  ): IKioskScheduleEntry | null {
    if (!this.lastKnownScheduleData || !this.lastKnownScheduleData.length) {
      return null;
    }

    return (
      this.lastKnownScheduleData.find((entry) => {
        const startTime = this.parseTimeWithRoomTimezone(entry.startTime);
        const endTime = this.parseTimeWithRoomTimezone(entry.endTime);
        return now.isSameOrAfter(startTime) && now.isBefore(endTime);
      }) || null
    );
  }

  /**
   * Calculate the target scroll position with offset for the time indicator
   */
  private calculateScrollPosition(): number {
    // Offset by 100px to show context above the current time
    return Math.max(0, this.currentTimeIndicatorPosition - 100);
  }

  /**
   * Perform smooth scrolling animation with cosine easing
   */
  private animateSmoothScroll(
    container: HTMLElement,
    targetPosition: number,
  ): void {
    const startPosition = container.scrollTop;
    const distance = targetPosition - startPosition;

    // Skip animation if already at position
    if (Math.abs(distance) < 5) return;

    // Much longer duration for slower animation
    const duration = 2000; // 2 seconds
    let startTime: number | null = null;

    const animate = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const elapsedTime = currentTime - startTime;

      // Calculate progress with capped maximum
      const progress = Math.min(elapsedTime / duration, 1);
      // Apply easing function
      const easedProgress = this.calculateEasing(progress);

      // Update scroll position
      container.scrollTop = startPosition + distance * easedProgress;

      // Continue animation if not complete
      if (elapsedTime < duration) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Calculate smooth cosine easing value for animation
   * Returns a value between 0 and 1 with a smooth acceleration/deceleration curve
   */
  private calculateEasing(progress: number): number {
    // Cosine easing function: 0.5 - cos(progress * PI) / 2
    return 0.5 - Math.cos(progress * Math.PI) / 2;
  }

  isCurrentEvent(event: IKioskScheduleEntry): boolean {
    const now = this.getTimeInRoomTimezone();
    const startTime = this.parseTimeWithRoomTimezone(event.startTime);
    const endTime = this.parseTimeWithRoomTimezone(event.endTime);
    return now.isSameOrAfter(startTime) && now.isBefore(endTime);
  }

  isPastSlot(event: IKioskScheduleEntry): boolean {
    return (
      event.isPast ??
      this.parseTimeWithRoomTimezone(event.endTime).isBefore(
        this.getTimeInRoomTimezone(),
      )
    );
  }

  // New methods that determine the current event index internally
  async onInitBookCurrent(): Promise<void> {
    const index = await this.findCurrentEventIndex();
    this.onInitBook(index);
  }

  async onEndMeetingCurrent(): Promise<void> {
    const index = await this.findCurrentEventIndex();
    this.onEndMeeting(index);
  }

  async onCheckInMeetingCurrent(): Promise<void> {
    const index = await this.findCurrentEventIndex();
    this.onCheckInMeeting(index);
  }

  // Helper method to find the current event index
  private async findCurrentEventIndex(): Promise<number> {
    return new Promise<number>((resolve) => {
      this.meetingRoomScheduleData$.pipe(take(1)).subscribe((data) => {
        // Find the entry that contains the current time
        const now = this.getTimeInRoomTimezone();
        const currentEntryIndex = data.findIndex((entry) => {
          const startTime = this.parseTimeWithRoomTimezone(entry.startTime);
          const endTime = this.parseTimeWithRoomTimezone(entry.endTime);
          return now.isSameOrAfter(startTime) && now.isBefore(endTime);
        });

        // If found a current entry, use its index, otherwise use 0
        const result = currentEntryIndex >= 0 ? currentEntryIndex : 0;
        resolve(result);
      });
    });
  }

  // Create a helper to parse ISO strings with the room's timezone
  private parseTimeWithRoomTimezone(timeString: string): moment.Moment {
    // Ensure timezone is available before parsing
    if (!this.timezone) {
      // Fallback or handle error appropriately if timezone isn't set yet
      console.warn("Timezone not set, using local time for parsing.");
      return moment(timeString);
    }
    return moment(timeString).tz(this.timezone);
  }

  checkingInEventIdx: number | null = null;
  checkIn(slotIdx: number): void {
    this.checkingInEventIdx = slotIdx;
    this.onCheckInMeeting(slotIdx);
  }

  checkInSelectedEntry(): void {
    if (!this.selectedScheduleEntry || !this.selectedScheduleEntry.id) {
      console.error("Cannot check in: no event selected or invalid event ID");
      return;
    }

    this.meetingRoomScheduleData$.pipe(take(1)).subscribe((data) => {
      // Find the index of the selected entry in the schedule data
      const selectedIdx = data.findIndex(
        (entry) => entry.id === this.selectedScheduleEntry?.id,
      );

      if (selectedIdx === -1) {
        console.error("Cannot find selected event in schedule data");
        return;
      }

      // Store the event ID before check-in
      const eventId = this.selectedScheduleEntry?.event?.id;

      // Use the existing check-in method with the found index
      this.isCheckingBookingIn = true;

      this.kioskModel.actions.dispatch.checkInBooking({
        roomId: this.meetingRoomId,
        eventId: eventId!,
      });

      race(
        this.kioskModel.actions.listen.checkInBookingSuccess$,
        this.kioskModel.actions.listen.checkInBookingFailure$.pipe(
          map((error) => {
            console.error("Check-in failed:", error);
            return false;
          }),
        ),
      )
        .pipe(take(1))
        .subscribe((response) => {
          this.isCheckingBookingIn = false;
          if (!response) return;

          // Update the LED status
          this.ledActivationService.setRoomLedStatus(
            MeetingRoomKioskStatus.OCCUPIED,
          );

          // Update the selectedScheduleEntry directly for immediate UI feedback
          if (this.selectedScheduleEntry) {
            this.selectedScheduleEntry = {
              ...this.selectedScheduleEntry,
              status: MeetingRoomKioskStatus.OCCUPIED,
            };

            // If there's an event object with checkIn info, update that too
            if (this.selectedScheduleEntry.event) {
              this.selectedScheduleEntry.event = {
                ...this.selectedScheduleEntry.event,
                checkIn: {
                  ...this.selectedScheduleEntry.event.checkIn,
                  checkedIn: true,
                },
              };
            }
          }

          // Force a reload of the data to get the updated status
          this.dataLoad$$.next(true);

          // Force change detection
          this.cdr.detectChanges();
        });
    });
  }

  onInitBookFromDetails(): void {
    if (!this.selectedScheduleEntry) {
      return;
    }

    // Find the index of the selected entry in the schedule data
    this.meetingRoomScheduleData$.pipe(take(1)).subscribe((data) => {
      const index = data.findIndex(
        (item) => item.id === this.selectedScheduleEntry?.id,
      );
      if (index !== -1) {
        this.onInitBook(index);
      }
    });
    // First deselect the entry
    this.deselectEntry();
  }
}
